import { find, includes } from 'lodash-es';

import type { ActiveStudySite, AuthUser } from 'common/auth/types';
import { AuthUserType, RoleTypeEnum } from 'common/graphql/types';
import { getStudyIdWithoutVersion } from 'common/utils/common.utils';

import type { DeviceHandoffRejectionReason, DeviceHandoffState } from '../state/types';

const ROLES_ALLOWED_TO_TAKE_DEVICE_BACK = [RoleTypeEnum.SiteInvestigator, RoleTypeEnum.SiteCoordinator];

export function checkValidSwitchUser(activeStudySite: ActiveStudySite, user: AuthUser): boolean {
    const { activeSite } = activeStudySite;
    if (!activeSite) {
        return false;
    }

    const matchingStudy = find(user?.userStudies, (e) => getStudyIdWithoutVersion(e.studyId) === activeSite.studyId);

    return Boolean(find(matchingStudy?.userStudySites, { siteId: activeSite.siteId }));
}

export type DeviceHandoffDecision =
    | { outcome: 'accepted' }
    | { outcome: 'rejected'; reason: DeviceHandoffRejectionReason };

const accepted: DeviceHandoffDecision = { outcome: 'accepted' };

function rejected(reason: DeviceHandoffRejectionReason): DeviceHandoffDecision {
    return { outcome: 'rejected', reason };
}

function evaluateRespondentCandidate(
    state: Extract<DeviceHandoffState, { phase: 'switch_to_respondent' }>,
    candidate: AuthUser,
    activeStudySite: ActiveStudySite
): DeviceHandoffDecision {
    const { target, gate, caregiverNames } = state;
    const hasCaregivers = caregiverNames.length > 0;
    const isCandidateInCaregiversList = caregiverNames.includes(candidate.username);
    const caregiverStatusMismatch = hasCaregivers && target.isCaregiverUser !== isCandidateInCaregiversList;

    if (caregiverStatusMismatch) {
        return rejected('respondentNotAuthorized');
    }

    const isTheExpectedRespondent = candidate.username === target.respondentID;

    if (isTheExpectedRespondent && gate.activityAlreadyCompleted) {
        return rejected('respondentAlreadyCompleted');
    }

    const hasStudySiteAccess = checkValidSwitchUser(activeStudySite, candidate);

    if (hasStudySiteAccess || isTheExpectedRespondent) {
        return accepted;
    }

    const isCandidatePatient = candidate.userType === AuthUserType.PATIENT;

    return isCandidatePatient ? rejected('respondentNotAuthorized') : rejected('userNotAuthorized');
}

function evaluateSiteUserCandidate(candidate: AuthUser, activeStudySite: ActiveStudySite): DeviceHandoffDecision {
    const isSiteRole = includes(ROLES_ALLOWED_TO_TAKE_DEVICE_BACK, candidate.roleVClaim?.name);

    if (!isSiteRole) {
        return rejected('userNotAuthorized');
    }

    return checkValidSwitchUser(activeStudySite, candidate) ? accepted : rejected('userNotAuthorized');
}

//TODO: it could be worthy to consider bigger refactor so there is no need for separate function to differentiate bteween states
export function evaluateDeviceHandoffCandidate(
    state: DeviceHandoffState,
    candidate: AuthUser,
    activeStudySite: ActiveStudySite
): DeviceHandoffDecision {
    switch (state.phase) {
        case 'switch_to_respondent':
            return evaluateRespondentCandidate(state, candidate, activeStudySite);
        case 'switch_to_siteuser':
            return evaluateSiteUserCandidate(candidate, activeStudySite);
        default:
            return rejected('userNotAuthorized');
    }
}
